import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { LeadConsent } from "@/components/LeadConsent";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/i18n/LocaleProvider";
import { getUtm, trackConversion } from "@/lib/tracking";
import { trackAnalyticsConversion } from "@/lib/analytics";

interface InlineLeadFormProps {
  /** Short machine-readable origin, e.g. "lead:custom-crm" or "case:cfab". */
  source: string;
  /** Human readable page title, used to build the email subject. */
  sourceLabel?: string;
  className?: string;
  /** Optional id so CTAs elsewhere on the page can scroll to the form. */
  id?: string;
}

/**
 * Compact, three-field lead form rendered directly inside a page's CTA block.
 *
 * Lead pages previously only linked to /contact — every extra click and page
 * transition cost conversions. Capturing name + email + a one-liner in place
 * removes that hop while keeping the same GDPR opt-in and anti-spam signals as
 * the full contact form.
 */
const InlineLeadForm = ({ source, sourceLabel, className = "", id }: InlineLeadFormProps) => {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { toast } = useToast();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const mountedAt = useRef<number>(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast({ title: t("inlineLead.fillRequired"), variant: "destructive" });
      return;
    }
    if (!consent) {
      toast({ title: t("leadConsent.required"), variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-contact", {
        body: {
          name: name.trim().slice(0, 100),
          email: email.trim().slice(0, 255),
          subject: t("inlineLead.subject", { source: sourceLabel || source }),
          message: message.trim().slice(0, 1000) || t("inlineLead.emptyMessage"),
          lang,
          consent,
          utm: { ...getUtm(), lead_source: source },
          hp: honeypot,
          elapsedMs: Date.now() - mountedAt.current,
        },
      });

      if (error || !data?.ok) {
        toast({ title: t("inlineLead.error"), variant: "destructive" });
        return;
      }

      setIsSubmitted(true);
      trackConversion("contact_submit");
      trackAnalyticsConversion("contact_submit", source);
    } catch {
      toast({ title: t("inlineLead.error"), variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div
        id={id}
        className={`rounded-2xl border border-accent/30 bg-card/60 backdrop-blur-sm p-8 text-center ${className}`}
      >
        <CheckCircle size={36} className="text-accent mx-auto mb-4" />
        <p className="text-[15px] sm:text-lg font-semibold text-foreground">{t("inlineLead.successTitle")}</p>
        <p className="mt-2 text-sm text-muted-foreground">{t("inlineLead.successBody")}</p>
      </div>
    );
  }

  return (
    <form
      id={id}
      onSubmit={handleSubmit}
      className={`rounded-2xl border border-border bg-card/60 backdrop-blur-sm p-6 md:p-8 text-left ${className}`}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${source}-name`} className="mono-label text-muted-foreground">
            {t("inlineLead.name")}
          </label>
          <Input
            id={`${source}-name`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            required
            autoComplete="name"
            placeholder={t("inlineLead.namePlaceholder")}
            className="mt-2 h-12"
          />
        </div>
        <div>
          <label htmlFor={`${source}-email`} className="mono-label text-muted-foreground">
            {t("inlineLead.email")}
          </label>
          <Input
            id={`${source}-email`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={255}
            required
            autoComplete="email"
            placeholder={t("inlineLead.emailPlaceholder")}
            className="mt-2 h-12"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor={`${source}-message`} className="mono-label text-muted-foreground">
          {t("inlineLead.message")}
        </label>
        <Textarea
          id={`${source}-message`}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={1000}
          rows={3}
          placeholder={t("inlineLead.messagePlaceholder")}
          className="mt-2 resize-none"
        />
      </div>

      {/* Honeypot — invisible to real users. */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="absolute left-[-9999px] w-px h-px opacity-0"
      />

      <LeadConsent checked={consent} onCheckedChange={setConsent} id={`${source}-consent`} className="mt-5" />

      <Button
        type="submit"
        disabled={isSubmitting}
        className="mt-6 w-full h-14 px-4 text-sm sm:text-base font-semibold btn-clip bg-primary text-primary-foreground btn-label-contrast hover:bg-primary"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            {t("inlineLead.sending")}
          </>
        ) : (
          <>
            {t("inlineLead.submit")}
            <Send size={18} />
          </>
        )}
      </Button>

      <p className="mt-4 font-mono text-[11px] tracking-[0.12em] text-muted-foreground/70 text-center">
        {t("inlineLead.note")}
      </p>
    </form>
  );
};

export default InlineLeadForm;
